/**
 * Gift Plate Preview Component
 *
 * Renders a gift plate using the EXACT same visual structure as RealisticPlate:
 * - Double rounded border (outer #2a2a2a + inner #888)
 * - Metallic gradient background
 * - Sauerkrauto font
 * - Same shadow/depth effects
 *
 * Plus gift-plate extras:
 * - Custom background colors/images
 * - Custom text & frame colors
 * - Optional vertical date column
 * - Configurable screw holes
 * - Editable text with emoji/symbol support
 */

import { useState, useRef, useEffect, useMemo } from 'react'

interface GiftPlatePreviewProps {
  text: string
  onTextChange: (text: string) => void
  backgroundColor: string
  backgroundImage?: string | null
  textColor: string
  frameColor: string
  showDate: boolean
  date?: { day: number; month: number; year: number }
  datePosition?: 'left' | 'right'
  holesEnabled: boolean
  holePositions: string[]
}

// ============================================================================
// GIFT BAND — Colored strip on the left (replaces EU band)
// ============================================================================

const GiftBand = ({ width, height, color }: { width: number; height: number; color: string }) => {
  const iconSize = Math.max(width * 0.5, 10)
  // Pick contrasting icon color
  const iconColor = isLightColor(color) ? '#333' : '#fff'

  return (
    <div
      className="flex-shrink-0 flex flex-col items-center justify-center"
      style={{
        width: `${width}px`,
        height: '100%',
        background: `linear-gradient(180deg, ${color} 0%, ${adjustColor(color, -30)} 100%)`,
        borderRadius: '4px 0 0 4px',
        padding: `${height * 0.08}px 0`,
        gap: `${height * 0.04}px`,
      }}
    >
      {/* Gift icon */}
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke={iconColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="8" width="18" height="13" rx="2" />
        <path d="M12 8v13" />
        <path d="M3 13h18" />
        <path d="M8 8c0-2 1-4 4-4s4 2 4 4" />
      </svg>
    </div>
  )
}

// ============================================================================
// HELPERS
// ============================================================================

function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  if (isNaN(num)) return hex
  const r = Math.max(0, Math.min(255, (num >> 16) + amount))
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount))
  const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount))
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}

function isLightColor(hex: string): boolean {
  const num = parseInt(hex.replace('#', ''), 16)
  if (isNaN(num)) return true
  const r = (num >> 16) & 0xFF
  const g = (num >> 8) & 0xFF
  const b = num & 0xFF
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GiftPlatePreview = ({
  text,
  onTextChange,
  backgroundColor,
  backgroundImage,
  textColor,
  frameColor,
  showDate,
  date,
  datePosition = 'right',
  holesEnabled,
  holePositions,
}: GiftPlatePreviewProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(text)
  const inputRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLDivElement>(null)

  // ── Responsive plate dimensions (same approach as RealisticPlate) ──
  const [W, setW] = useState(580)

  useEffect(() => {
    const update = () => {
      const sw = window.innerWidth
      if (sw < 640) {
        setW(Math.min(580, sw - 32))
      } else if (sw < 1024) {
        setW(Math.min(580, sw - 48))
      } else {
        setW(580)
      }
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // ── Layout constants (matching RealisticPlate exactly) ──
  const ar = 520 / 110 // Standard German plate aspect ratio ≈ 4.73
  const H = W / ar
  const pad = 6
  const innerBorderW = 3

  // Content area dimensions
  const contentH = H - pad * 2 - innerBorderW * 2
  const bandW = (W - pad * 2) * 0.07 // Gift band width (slightly narrower than EU band)

  // Date column
  const DATE_COL_W = showDate ? Math.max(60, Math.round(W * 0.13)) : 0

  // Font sizing (Sauerkrauto-based, matching RealisticPlate calculations)
  const baseFontSize = contentH * 0.65
  const fs = Math.min(Math.max(baseFontSize, 16), 72)
  const charW = fs * 0.52 // Sauerkrauto character width

  // Available text width
  const textAreaPadX = Math.max(fs * 0.2, 8)
  const availableTextW = W - pad * 2 - innerBorderW * 2 - bandW - DATE_COL_W - textAreaPadX * 2

  // ── Text processing ──
  const convertToUppercase = (str: string): string => {
    return str.split('').map(char =>
      /[a-zA-Z]/.test(char) ? char.toUpperCase() : char
    ).join('')
  }

  const displayText = text ? convertToUppercase(text) : ''

  useEffect(() => { setEditText(text) }, [text])
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // ── Text font size auto-scaling ──
  const textFontSize = useMemo(() => {
    if (!text) return fs
    const estimatedW = text.length * charW
    if (estimatedW > availableTextW) {
      const scale = availableTextW / estimatedW
      return Math.floor(Math.max(fs * 0.4, fs * scale))
    }
    return fs
  }, [text, fs, charW, availableTextW])

  const letterSpacing = `${textFontSize * 0.04}px`

  const isTextAtLimit = useMemo(() => {
    if (!text) return false
    return text.length * (textFontSize * 0.52) >= availableTextW * 0.9
  }, [text, textFontSize, availableTextW])

  // ── Font style (matching RealisticPlate exactly) ──
  const plateFont: React.CSSProperties = {
    fontFamily: "'Sauerkrauto', 'Arial Black', 'Segoe UI Black', Impact, Arial, sans-serif",
    fontWeight: 'normal',
    fontSize: `${textFontSize}px`,
    letterSpacing,
    lineHeight: 1,
    color: textColor,
  }

  // ── Event handlers ──
  const handleTextClick = () => {
    setIsEditing(true)
    setEditText(text)
  }

  const handleTextBlur = () => {
    setIsEditing(false)
    if (editText.length <= 10) {
      onTextChange(convertToUppercase(editText))
    } else {
      setEditText(text)
    }
  }

  const handleTextKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTextBlur()
    else if (e.key === 'Escape') { setEditText(text); setIsEditing(false) }
  }

  // ── Date formatting ──
  const formatDate = (): string[] => {
    if (!date) return []
    const day = String(date.day).padStart(2, '0')
    const month = String(date.month).padStart(2, '0')
    const year = String(date.year)
    return [day, '—', month, '—', year]
  }

  // ── Screw holes ──
  const hasHole = (pos: string) => holesEnabled && holePositions.includes(pos)
  const holeSz = Math.max(contentH * 0.08, 8)
  const holeOff = Math.max(contentH * 0.12, 10)

  const getHolePos = (pos: string): React.CSSProperties => {
    const o = holeOff
    const base: React.CSSProperties = { position: 'absolute', width: `${holeSz}px`, height: `${holeSz}px` }
    switch (pos) {
      case 'top-left': return { ...base, top: `${o}px`, left: `${o}px` }
      case 'top-right': return { ...base, top: `${o}px`, right: `${o}px` }
      case 'top-center': return { ...base, top: `${o}px`, left: '50%', transform: 'translateX(-50%)' }
      case 'bottom-left': return { ...base, bottom: `${o}px`, left: `${o}px` }
      case 'bottom-right': return { ...base, bottom: `${o}px`, right: `${o}px` }
      case 'bottom-center': return { ...base, bottom: `${o}px`, left: '50%', transform: 'translateX(-50%)' }
      case 'center-left': return { ...base, top: '50%', left: `${o}px`, transform: 'translateY(-50%)' }
      case 'center-right': return { ...base, top: '50%', right: `${o}px`, transform: 'translateY(-50%)' }
      case 'center': return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
      default: return base
    }
  }

  const HOLE_IDS = ['top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right', 'bottom-center', 'center-left', 'center-right', 'center']

  // ── Plate background ──
  const isCustomBg = backgroundImage != null
  const isWhite = backgroundColor === '#FFFFFF' && !isCustomBg
  const isBlack = backgroundColor === '#000000' && !isCustomBg

  const getContentBg = (): string => {
    if (isCustomBg && backgroundImage) return backgroundImage
    if (isWhite) return 'linear-gradient(175deg, #fefefe 0%, #f8f6f0 40%, #f5f2ec 100%)'
    if (isBlack) return 'linear-gradient(175deg, #1a1a1a 0%, #111 40%, #0a0a0a 100%)'
    return `linear-gradient(175deg, ${backgroundColor} 0%, ${adjustColor(backgroundColor, -10)} 50%, ${adjustColor(backgroundColor, -20)} 100%)`
  }

  // Inner border color — adapt frame color for visibility
  const innerBorderColor = frameColor === '#FFFFFF' ? '#ccc' : frameColor === '#000000' ? '#555' : frameColor

  // Date font sizes
  const dateFontSz = Math.max(contentH * 0.2, 14)
  const dateSepSz = Math.max(contentH * 0.14, 10)

  // ── RENDER ──────────────────────────────────────────────────────────────
  return (
    <div className="w-full relative">
      {isTextAtLimit && (
        <p className="text-xs text-yellow-400 mb-2 flex items-center gap-1 justify-center">
          <span>&#9888;</span>
          <span>Text erreicht Layout-Grenze</span>
        </p>
      )}

      {/* ── Plate — exact RealisticPlate structure ── */}
      <div
        className="mx-auto"
        style={{
          width: `${W}px`,
          height: `${H}px`,
          maxWidth: '100%',
          // Outer border — same as RealisticPlate
          border: '3px solid #2a2a2a',
          borderRadius: '8px',
          padding: `${pad}px`,
          // Outer background gradient — same metallic base
          background: 'linear-gradient(175deg, #fefefe 0%, #f5f2ec 50%, #f0ede6 100%)',
          // Shadow — same as RealisticPlate
          boxShadow: '0 6px 28px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.6)',
          overflow: 'visible',
        }}
      >
        {/* Inner border frame — same as RealisticPlate */}
        <div
          style={{
            width: '100%',
            height: '100%',
            border: `${innerBorderW}px solid ${innerBorderColor}`,
            borderRadius: '5px',
            overflow: 'visible',
          }}
        >
          {/* Plate content — flex row like RealisticPlate */}
          <div
            className="flex"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '4px',
              background: getContentBg(),
              position: 'relative',
            }}
          >
            {/* ── Gift Band (left side, like EU band) ── */}
            {(!showDate || datePosition !== 'left') && (
              <GiftBand width={bandW} height={contentH} color={frameColor === '#FFFFFF' ? '#e91e63' : frameColor} />
            )}

            {/* ── Date Column LEFT ── */}
            {showDate && datePosition === 'left' && date && (
              <div
                className="flex-shrink-0 flex flex-col items-center justify-center"
                style={{
                  width: `${DATE_COL_W}px`,
                  height: '100%',
                  borderRight: `2px solid ${innerBorderColor}40`,
                }}
              >
                {formatDate().map((item, i) => {
                  const isSep = item === '—'
                  return (
                    <div
                      key={i}
                      style={{
                        ...plateFont,
                        fontSize: isSep ? `${dateSepSz}px` : `${dateFontSz}px`,
                        fontWeight: isSep ? 400 : undefined,
                        lineHeight: isSep ? '0.8' : '1.2',
                        margin: isSep ? '1px 0' : '0',
                        color: isSep ? `${textColor}80` : textColor,
                        letterSpacing: '0px',
                      }}
                    >
                      {item}
                    </div>
                  )
                })}
              </div>
            )}

            {/* ── Main Text Area ── */}
            <div
              className="flex-1 flex items-center justify-center h-full"
              style={{ padding: `0 ${textAreaPadX}px`, position: 'relative' }}
            >
              {/* Screw holes (rendered inside content area) */}
              {HOLE_IDS.map(pos =>
                hasHole(pos) ? (
                  <div
                    key={pos}
                    style={{
                      ...getHolePos(pos),
                      borderRadius: '50%',
                      background: 'radial-gradient(circle at 35% 35%, #888 0%, #555 40%, #333 70%, #1a1a1a 100%)',
                      boxShadow: `
                        inset 0 1px 3px rgba(0,0,0,0.8),
                        inset 0 -1px 1px rgba(255,255,255,0.15),
                        0 1px 2px rgba(0,0,0,0.3),
                        0 0 0 ${Math.max(holeSz * 0.15, 1.5)}px #666
                      `,
                      zIndex: 15,
                    }}
                  >
                    {/* Phillips screw cross */}
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: `${holeSz * 0.5}px`, height: `${Math.max(holeSz * 0.08, 1)}px`, background: '#222', position: 'absolute', borderRadius: '1px' }} />
                      <div style={{ width: `${Math.max(holeSz * 0.08, 1)}px`, height: `${holeSz * 0.5}px`, background: '#222', position: 'absolute', borderRadius: '1px' }} />
                    </div>
                  </div>
                ) : null
              )}

              {/* Text content */}
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editText}
                  onChange={(e) => {
                    if (e.target.value.length <= 10) {
                      setEditText(convertToUppercase(e.target.value))
                    }
                  }}
                  onBlur={handleTextBlur}
                  onKeyDown={handleTextKeyDown}
                  className="bg-transparent border-2 border-dashed border-current outline-none text-center"
                  style={{
                    ...plateFont,
                    width: '100%',
                    caretColor: textColor,
                  }}
                  maxLength={10}
                />
              ) : (
                <div
                  ref={textRef}
                  onClick={handleTextClick}
                  className="whitespace-nowrap cursor-text transition-opacity text-center"
                  style={displayText ? plateFont : {
                    ...plateFont,
                    opacity: 0.3,
                    fontSize: `${textFontSize * 0.5}px`,
                  }}
                >
                  {displayText || 'TEXT EINGEBEN'}
                </div>
              )}
            </div>

            {/* ── Date Column RIGHT ── */}
            {showDate && datePosition === 'right' && date && (
              <div
                className="flex-shrink-0 flex flex-col items-center justify-center"
                style={{
                  width: `${DATE_COL_W}px`,
                  height: '100%',
                  borderLeft: `2px solid ${innerBorderColor}40`,
                }}
              >
                {formatDate().map((item, i) => {
                  const isSep = item === '—'
                  return (
                    <div
                      key={i}
                      style={{
                        ...plateFont,
                        fontSize: isSep ? `${dateSepSz}px` : `${dateFontSz}px`,
                        fontWeight: isSep ? 400 : undefined,
                        lineHeight: isSep ? '0.8' : '1.2',
                        margin: isSep ? '1px 0' : '0',
                        color: isSep ? `${textColor}80` : textColor,
                        letterSpacing: '0px',
                      }}
                    >
                      {item}
                    </div>
                  )
                })}
              </div>
            )}

            {/* ── Gift Band RIGHT (when date is on left) ── */}
            {showDate && datePosition === 'left' && (
              <GiftBand width={bandW} height={contentH} color={frameColor === '#FFFFFF' ? '#e91e63' : frameColor} />
            )}
          </div>
        </div>
      </div>

      {/* Size label — same style as RealisticPlate */}
      <div className="text-center text-xs text-gray-500" style={{ maxWidth: `${W}px`, margin: '4px auto 0' }}>
        520 x 110 mm (Einzeilig) &bull; Klicken zum Bearbeiten &bull; {text.length}/10 Zeichen
      </div>
    </div>
  )
}

export default GiftPlatePreview
